// Lightweight toast — no extra dependency. One singleton container is
// teleported into <body> the first time `notify()` fires, and toasts
// stack inside it. Use the `$notify` global ($app.config.globalProperties)
// or import { notify } directly in <script setup>.
//
// API mirrors what we actually use today across HeroSection / Journey /
// Payments / SeatDetails:
//   notify.error('msg')
//   notify.success('msg')
//   notify.info('msg')
//   notify.warn('msg')

import { h, render, createVNode, ref } from "vue";

const queue = ref([]);
let nextId = 1;
let mounted = false;
let host = null;

function ensureMounted() {
  if (mounted) return;
  host = document.createElement("div");
  host.id = "ab-toast-host";
  host.setAttribute("aria-live", "polite");
  host.style.cssText = [
    "position:fixed",
    "z-index:9999",
    "top:16px",
    "right:16px",
    "display:flex",
    "flex-direction:column",
    "gap:8px",
    "max-width:calc(100vw - 32px)",
    "pointer-events:none",
  ].join(";");
  document.body.appendChild(host);
  mounted = true;

  const Host = {
    setup() {
      return () =>
        h(
          "div",
          {},
          queue.value.map((t) =>
            h(
              "div",
              {
                key: t.id,
                role: t.kind === "error" ? "alert" : "status",
                style: toastStyle(t.kind),
                onClick: () => dismiss(t.id),
              },
              [
                h(
                  "span",
                  { style: "font-size:18px;line-height:1;flex:0 0 auto;" },
                  iconFor(t.kind)
                ),
                h(
                  "span",
                  { style: "flex:1 1 auto;font:600 14px/1.35 var(--font-sans, sans-serif)" },
                  t.message
                ),
              ]
            )
          )
        );
    },
  };
  render(createVNode(Host), host);
}

function toastStyle(kind) {
  const palette = {
    success: ["#16a34a", "#fff"],
    error:   ["#dc2626", "#fff"],
    warn:    ["#f59e0b", "#fff"],
    info:    ["#1158ab", "#fff"],
  }[kind] || ["#0f172a", "#fff"];
  return [
    `background:${palette[0]}`,
    `color:${palette[1]}`,
    "padding:12px 14px",
    "border-radius:10px",
    "box-shadow:0 8px 24px rgba(15,23,42,.16)",
    "display:flex",
    "align-items:center",
    "gap:10px",
    "min-width:260px",
    "max-width:420px",
    "pointer-events:auto",
    "cursor:pointer",
    "transform:translateZ(0)",
  ].join(";");
}

function iconFor(kind) {
  return {
    success: "✓",
    error:   "✕",
    warn:    "!",
    info:    "i",
  }[kind] || "•";
}

function push(kind, message, ttl = 4500) {
  ensureMounted();
  const id = nextId++;
  queue.value.push({ id, kind, message: String(message ?? "") });
  if (ttl) setTimeout(() => dismiss(id), ttl);
  return id;
}

function dismiss(id) {
  queue.value = queue.value.filter((t) => t.id !== id);
}

export const notify = {
  success: (m, ttl) => push("success", m, ttl),
  error:   (m, ttl) => push("error",   m, ttl ?? 6000),
  warn:    (m, ttl) => push("warn",    m, ttl),
  info:    (m, ttl) => push("info",    m, ttl),
  dismiss,
};

export default {
  install(app) {
    app.config.globalProperties.$notify = notify;
  },
};
